import {
	type Accessor,
	batch,
	createComputed,
	createResource,
	createSignal,
	DEV,
	getOwner,
	onCleanup,
	type Setter,
} from "solid-js";

const isDev = !!DEV;
export const noop = (() => void 0) as (...a: any[]) => void;

/**
 * Solid's `onCleanup` that doesn't warn in development if used outside of a component.
 */
export const tryOnCleanup: typeof onCleanup = isDev
	? (fn) => (getOwner() ? onCleanup(fn) : fn)
	: onCleanup;

export function createInfiniteScroll<T>(
	fetcher: (page: number) => Promise<T[]>,
	initialPage: number = 1,
): [
	pages: Accessor<T[]>,
	loader: (el: Element) => void,
	options: {
		page: Accessor<number>;
		setPage: Setter<number>;
		setPages: Setter<T[]>;
		end: Accessor<boolean>;
		setEnd: Setter<boolean>;
		reset: () => void;
	},
	resourceActions: { 
		mutate: (value: (prev: T[]) => T[]) => void;
		loading: Accessor<boolean>; 
		error: Accessor<string>; 
		restoreLastMutation: () => void;
	},
] {
	const [pages, setPages] = createSignal<T[]>([]);
	const [page, setPage] = createSignal(initialPage);
	const [end, setEnd] = createSignal(false);
	const [loading, setLoading] = createSignal(true);
	const [error, setError] = createSignal<string | undefined>(undefined);
	const [previousPages, setPreviousPages] = createSignal<T[]>([]);
	const [version, setVersion] = createSignal(0); // Used to force refetch on reset
	let lastAppliedKey: string | null = null;

	let add: (el: Element) => void = noop;
	const io = new IntersectionObserver((e) => {
		if (e.length > 0 && e[0].isIntersecting && !end() && !contents.loading) {
			setPage((p) => p + 1);
		}
	});

	onCleanup(() => io.disconnect());
	add = (el: Element) => {
		io.observe(el);
		tryOnCleanup(() => io.unobserve(el));
	};

	const reset = () => {
		batch(() => {
			lastAppliedKey = null;
			setPages([]);
			setEnd(false);
			setPage(initialPage);
			setLoading(true);
			setError(undefined);
			setPreviousPages([]);
			setVersion((v) => v + 1); // Increment version to force refetch
		});
	};

	// Include version in source so reset() triggers a refetch even if page is already initialPage
	const [contents] = createResource(
		() => ({ page: page(), version: version() }),
		(source) => fetcher(source.page),
	);
	createComputed(() => {
		const fetchKey = `${page()}:${version()}`;
		if (contents.loading) {
			return;
		}
		const content = contents();
		if (content === undefined) {
			return;
		}
		if (lastAppliedKey === fetchKey) {
			return;
		}
		lastAppliedKey = fetchKey;
		setError(contents.error);
		setLoading(false);
		batch(() => {
			if (content.length === 0) setEnd(true);
			setPages((p) => [...p, ...content]);
		});
	});
	
	const mutate = (value: (prev: T[]) => T[]) => {
		setPreviousPages(pages());
		setPages(value(pages()));
	};

	const restoreLastMutation = () => {
		setPages(previousPages());
	};

	return [
		pages,
		add,
		{
			page,
			setPage,
			setPages,
			end,
			setEnd,
			reset,
		},
		{
			mutate,
			loading,
			error,
			restoreLastMutation,
		},
	];
}
